import { NgFor } from "@angular/common";
import { Component, OnInit } from "@angular/core";
import { ReactiveFormsModule, FormsModule } from "@angular/forms";
import Swal from "sweetalert2";

import { GloToggleComponent } from "../../../../shared/glo-toggle/glo-toggle.component";
import { ApiService } from "../../../../shared/services/api/api.service";
import { API_ENDPOINTS } from "../../../../shared/services/api/api-endpoints";
import { LoaderComponent } from "../../../../shared/components/loader/loader.component";

@Component({
  selector: "business-account-notification",
  standalone: true,
  imports: [
    NgFor,
    GloToggleComponent,
    ReactiveFormsModule,
    FormsModule,
    LoaderComponent,
  ],
  templateUrl: "./notification.component.html",
  styleUrl: "./notification.component.scss",
})
export class BusinessAccountNotificationComponent implements OnInit {
  isLoading = false;

  notificationSettings = [
    {
      key: "comments",
      label: "Comments",
      description:
        "Notifications for comments, customer replies, and internal notes.",
      channels: [
        { key: "push", label: "Push", checked: false },
        { key: "email", label: "Email", checked: false },
        { key: "sms", label: "SMS", checked: false },
      ],
    },
    {
      key: "tags",
      label: "Tags",
      description:
        "Notifications when someone tags you in a comment, post, or story.",
      channels: [
        { key: "push", label: "Push", checked: false },
        { key: "email", label: "Email", checked: false },
        { key: "sms", label: "SMS", checked: false },
      ],
    },
    {
      key: "reminders",
      label: "Reminders",
      description:
        "Reminders for appointments, missed updates, and account tasks.",
      channels: [
        { key: "push", label: "Push", checked: false },
        { key: "email", label: "Email", checked: false },
        { key: "sms", label: "SMS", checked: false },
      ],
    },
    {
      key: "activity",
      label: "More activity about you",
      description:
        "Notifications for profile activity, reactions, likes, and engagement.",
      channels: [
        { key: "push", label: "Push", checked: false },
        { key: "email", label: "Email", checked: false },
        { key: "sms", label: "SMS", checked: false },
      ],
    },
  ];

  constructor(private apiService: ApiService) {}

  ngOnInit(): void {
    this.getUserDetails();
  }

  handleSave(): void {
    this.isLoading = true;

    const payload: any = {};

    this.notificationSettings.forEach((group) => {
      const groupKey =
        group.key === "activity" ? "more_activity_about_you" : group.key;

      payload[groupKey] = {};

      group.channels.forEach((channel) => {
        payload[groupKey][channel.key] = channel.checked;
      });
    });

    this.apiService
      .request(
        API_ENDPOINTS.owner.accountSettings.notifications,
        "POST",
        payload,
      )
      .subscribe({
        next: () => {
          this.isLoading = false;

          // Swal.fire({
          //   title: "Success!",
          //   text: "Notification preferences updated successfully!",
          //   icon: "success",
          //   timer: 5000,
          //   showConfirmButton: false,
          //   toast: true,
          //   position: "top-end",
          //   customClass: {
          //     container: "!z-[999999999]",
          //   },
          // });

          this.getUserDetails();
        },
        error: (error) => {
          this.isLoading = false;
          console.error("Notification save error", error);

          Swal.fire({
            title: "Failed to Save",
            text: "Something went wrong while saving notification preferences.",
            icon: "error",
            timer: 5000,
            showConfirmButton: false,
            toast: true,
            position: "top-end",
            customClass: {
              container: "!z-[999999999]",
            },
          });
        },
      });
  }

  getUserDetails(): void {
    this.isLoading = true;

    this.apiService
      .request<any>(API_ENDPOINTS.owner.accountSettings.notifications, "GET")
      .subscribe({
        next: (response) => {
          this.isLoading = false;

          const data = response?.data;
          if (!data) return;

          this.notificationSettings.forEach((group) => {
            const groupKey =
              group.key === "activity" ? "more_activity_about_you" : group.key;

            if (!data[groupKey]) return;

            group.channels.forEach((channel) => {
              if (
                Object.prototype.hasOwnProperty.call(
                  data[groupKey],
                  channel.key,
                )
              ) {
                channel.checked = data[groupKey][channel.key];
              }
            });
          });
        },
        error: (error) => {
          console.error("Notification load error", error);
          this.isLoading = false;
        },
      });
  }

  getChannelDescription(channelKey: string): string {
    switch (channelKey) {
      case "push":
        return "Dashboard alerts";
      case "email":
        return "Inbox updates";
      case "sms":
        return "Text alerts";
      default:
        return "Notification";
    }
  }

  toggleChannel(groupKey: string, channelKey: string): void {
    const group = this.notificationSettings.find(
      (item) => item.key === groupKey,
    );
    if (!group) return;

    const channel = group.channels.find((item) => item.key === channelKey);
    if (!channel) return;

    channel.checked = !channel.checked;
  }
}
